/**
 * Partners Section - Trusted By Industry Leaders
 * ===============================================
 * Innovative orbital design with floating partner cards
 * WOW factor: orbiting logos, 3D tilt, particle connections
 */

import React, { useState, useEffect, useRef } from 'react';
import { config } from '../../data/config';
import './Partners.css';

// Partner Card Component with 3D effect
const PartnerCard = ({ partner, index, isInView }) => {
  const cardRef = useRef(null);
  const [isVisible, setIsVisible] = useState(false);
  const [mousePos, setMousePos] = useState({ x: 0, y: 0 });

  useEffect(() => {
    if (isInView) {
      const timer = setTimeout(() => setIsVisible(true), 300 + index * 150);
      return () => clearTimeout(timer);
    }
  }, [isInView, index]);

  const handleMouseMove = (e) => {
    if (!cardRef.current) return;
    const rect = cardRef.current.getBoundingClientRect();
    const x = (e.clientX - rect.left - rect.width / 2) / 20;
    const y = (e.clientY - rect.top - rect.height / 2) / 20;
    setMousePos({ x, y });
  };

  const handleMouseLeave = () => {
    setMousePos({ x: 0, y: 0 });
  };

  return (
    <a
      href={partner.website}
      target="_blank"
      rel="noopener noreferrer"
      ref={cardRef}
      className={`partner-card ${isVisible ? 'in-view' : ''}`}
      style={{
        '--partner-color': partner.color,
        '--delay': `${index * 0.1}s`,
        transform: `perspective(1000px) rotateX(${-mousePos.y}deg) rotateY(${mousePos.x}deg)`
      }}
      onMouseMove={handleMouseMove}
      onMouseLeave={handleMouseLeave}
    >
      {/* Animated Border */}
      <div className="partner-border">
        <div className="border-glow" />
      </div>
      
      {/* Logo Container */}
      <div className="partner-logo-wrap">
        <div className="partner-logo-ring" />
        <div className="partner-logo">
          <img 
            src={partner.logo} 
            alt={partner.name}
            onError={(e) => {
              e.target.style.display = 'none';
              e.target.nextSibling.style.display = 'flex';
            }}
          />
          <div className="partner-logo-fallback">
            {partner.name.charAt(0)}
          </div>
        </div>
      </div>

      {/* Info */}
      <div className="partner-info">
        <h3>{partner.name}</h3>
        <p>{partner.description}</p>
      </div>

      {/* Hover Effect */}
      <div className="partner-shine" />
      
      {/* Corner Accents */}
      <div className="partner-corner partner-corner-tl" />
      <div className="partner-corner partner-corner-tr" />
      <div className="partner-corner partner-corner-bl" />
      <div className="partner-corner partner-corner-br" />
    </a>
  );
};

// Particle Network Background
const PartnersParticles = () => {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    let animationId;
    let particles = [];
    let nodes = [];

    const resize = () => {
      canvas.width = canvas.offsetWidth;
      canvas.height = canvas.offsetHeight;
    };

    const colors = ['#D4A853', '#00D4FF', '#A855F7', '#10B981', '#F97316', '#EC4899'];

    const createElements = () => {
      const isMobile = window.innerWidth < 768;
      particles = [];
      nodes = [];

      // Create floating particles
      for (let i = 0; i < (isMobile ? 30 : 60); i++) {
        particles.push({
          x: Math.random() * canvas.width,
          y: Math.random() * canvas.height,
          size: Math.random() * 2 + 0.5,
          speedX: (Math.random() - 0.5) * 0.3,
          speedY: (Math.random() - 0.5) * 0.3,
          color: colors[Math.floor(Math.random() * colors.length)],
          opacity: Math.random() * 0.5 + 0.2
        });
      }

      // Create larger nodes
      for (let i = 0; i < (isMobile ? 5 : 10); i++) {
        nodes.push({
          x: Math.random() * canvas.width,
          y: Math.random() * canvas.height,
          size: Math.random() * 4 + 3,
          speedX: (Math.random() - 0.5) * 0.2,
          speedY: (Math.random() - 0.5) * 0.2,
          color: colors[Math.floor(Math.random() * colors.length)],
          pulse: Math.random() * Math.PI * 2
        });
      }
    };

    const animate = () => {
      ctx.fillStyle = 'rgba(5, 5, 5, 0.1)';
      ctx.fillRect(0, 0, canvas.width, canvas.height);

      // Draw particles
      particles.forEach(p => {
        p.x += p.speedX;
        p.y += p.speedY;

        if (p.x < 0) p.x = canvas.width;
        if (p.x > canvas.width) p.x = 0;
        if (p.y < 0) p.y = canvas.height;
        if (p.y > canvas.height) p.y = 0;

        ctx.beginPath();
        ctx.arc(p.x, p.y, p.size, 0, Math.PI * 2);
        ctx.fillStyle = p.color + Math.floor(p.opacity * 255).toString(16).padStart(2, '0');
        ctx.fill();
      });

      // Draw nodes with connections
      nodes.forEach((node, i) => {
        node.x += node.speedX;
        node.y += node.speedY;
        node.pulse += 0.02;

        if (node.x < 0 || node.x > canvas.width) node.speedX *= -1;
        if (node.y < 0 || node.y > canvas.height) node.speedY *= -1;

        // Draw connections to nearby nodes
        nodes.forEach((other, j) => {
          if (i < j) {
            const dx = node.x - other.x;
            const dy = node.y - other.y;
            const dist = Math.sqrt(dx * dx + dy * dy);
            if (dist < 200) {
              const gradient = ctx.createLinearGradient(node.x, node.y, other.x, other.y);
              gradient.addColorStop(0, node.color + '30');
              gradient.addColorStop(1, other.color + '30');
              ctx.beginPath();
              ctx.moveTo(node.x, node.y);
              ctx.lineTo(other.x, other.y);
              ctx.strokeStyle = gradient;
              ctx.lineWidth = 1;
              ctx.stroke();
            }
          }
        });

        // Draw node with pulse
        const pulseSize = node.size + Math.sin(node.pulse) * 2;
        ctx.beginPath();
        ctx.arc(node.x, node.y, pulseSize, 0, Math.PI * 2);
        ctx.fillStyle = node.color + '80';
        ctx.fill();

        // Outer glow
        ctx.beginPath();
        ctx.arc(node.x, node.y, pulseSize + 5, 0, Math.PI * 2);
        ctx.fillStyle = node.color + '20';
        ctx.fill();
      });

      animationId = requestAnimationFrame(animate);
    };

    resize();
    createElements();
    animate();

    window.addEventListener('resize', () => {
      resize();
      createElements();
    });

    return () => {
      cancelAnimationFrame(animationId);
      window.removeEventListener('resize', resize);
    };
  }, []);

  return <canvas ref={canvasRef} className="partners-canvas" />;
};

const Partners = () => {
  const sectionRef = useRef(null);
  const [isInView, setIsInView] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsInView(true);
          observer.disconnect();
        }
      },
      { threshold: 0.15 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  return (
    <section className="partners-section" ref={sectionRef} id="partners">
      {/* Background - CSS particles */}
      <div className="partners-bg">
        {/* Subtle glows */}
        <div className="partners-glow partners-glow-1" />
        <div className="partners-glow partners-glow-2" />
        
        {/* Particles - 20 bigger particles */}
        <div className="partners-particles">
          {[...Array(20)].map((_, i) => (
            <span 
              key={i}
              className="particle"
              style={{
                left: `${Math.random() * 100}%`,
                top: `${Math.random() * 100}%`,
                '--size': `${4 + Math.random() * 6}px`,
                '--duration': `${12 + Math.random() * 15}s`,
                '--delay': `${Math.random() * 8}s`,
                '--x-move': `${-40 + Math.random() * 80}px`,
                '--y-move': `${-100 + Math.random() * 50}px`
              }}
            />
          ))}
        </div>
        <div className="partners-grid-bg" />
        <div className="partners-vignette" />
      </div>

      <div className="partners-container">
        {/* Header */}
        <div className={`partners-header ${isInView ? 'in-view' : ''}`}>
          <div className="section-badge">
            <span className="badge-icon">
              <i className="fas fa-handshake" />
            </span>
            <span>Trusted Partners</span>
          </div>
          <h2 className="section-title">
            Powering <span className="text-highlight">Innovation</span> Together
          </h2>
          <p className="section-subtitle">
            We've partnered with industry leaders to deliver exceptional solutions
          </p>
        </div>

        {/* Partners Orbital Grid */}
        <div className="partners-orbital">
          {/* Central Logo */}
          <div className={`central-logo ${isInView ? 'in-view' : ''}`}>
            <div className="central-ring central-ring-1" />
            <div className="central-ring central-ring-2" />
            <div className="central-ring central-ring-3" />
            <div className="central-core">
              <span>D</span>
            </div>
          </div>

          {/* Partner Cards */}
          <div className="partners-grid">
            {config.partners.map((partner, index) => (
              <PartnerCard
                key={partner.id}
                partner={partner}
                index={index}
                isInView={isInView}
              />
            ))}
          </div>
        </div>

        {/* CTA */}
        <div className={`partners-cta ${isInView ? 'in-view' : ''}`}>
          <p>Want to become a partner?</p>
          <a
            href={config.social.whatsapp}
            target="_blank"
            rel="noopener noreferrer"
            className="btn-partner"
          >
            <i className="fab fa-whatsapp" />
            <span>Let's Connect</span>
          </a>
        </div>
      </div>
    </section>
  );
};

export default Partners;
